import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

export const Parametres = () => {
  const { user, updatePassword } = useAuth();
  const toast = useToast();
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [apiUrl, setApiUrl] = useState(process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1');

  const handlePw = async () => {
    if (!pwForm.currentPassword || !pwForm.newPassword) { toast.error('Remplissez tous les champs.'); return; }
    if (pwForm.newPassword !== pwForm.confirmPassword) { toast.error('Les mots de passe ne correspondent pas.'); return; }
    if (pwForm.newPassword.length < 8) { toast.error('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    setSaving(true);
    try {
      await updatePassword(pwForm.currentPassword, pwForm.newPassword);
      toast.success('Mot de passe changé. Reconnectez-vous.');
    } catch (err) {
      toast.error(err.message || 'Erreur lors du changement.');
    }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 640 }}>
      {/* PROFILE */}
      <div className="card section-gap">
        <div className="card-header"><div className="card-title">Profil administrateur</div></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: 'linear-gradient(135deg, var(--accent), #80d040)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#0a0c10' }}>
            {user?.name?.[0]}
          </div>
          <div>
            <div style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 700 }}>{user?.name}</div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>{user?.email}</div>
            <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 3 }}>🔐 Administrateur LAVEX</div>
          </div>
        </div>
        <div className="inline-alert inline-alert-blue">
          <span>ℹ</span>
          <span style={{ fontSize: 12 }}>Les modifications du profil admin doivent être effectuées directement en base de données pour des raisons de sécurité.</span>
        </div>
      </div>

      {/* CHANGE PASSWORD */}
      <div className="card section-gap">
        <div className="card-header"><div className="card-title">Changer le mot de passe</div></div>
        <div className="form-group">
          <label className="form-label">Mot de passe actuel</label>
          <input className="form-input" type="password" placeholder="••••••••" value={pwForm.currentPassword} onChange={e => setPwForm(p => ({ ...p, currentPassword: e.target.value }))} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Nouveau mot de passe</label>
            <input className="form-input" type="password" placeholder="••••••••" value={pwForm.newPassword} onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirmer</label>
            <input className="form-input" type="password" placeholder="••••••••" value={pwForm.confirmPassword} onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))} />
          </div>
        </div>
        <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 16 }}>
          <span>⚠️</span>
          <span style={{ fontSize: 12 }}>Le changement invalide toutes les sessions actives. Vous serez déconnecté automatiquement.</span>
        </div>
        <button className="btn btn-primary" onClick={handlePw} disabled={saving}>
          {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Changer le mot de passe'}
        </button>
      </div>

      {/* API CONFIG */}
      <div className="card section-gap">
        <div className="card-header"><div className="card-title">Configuration API</div></div>
        <div className="form-group">
          <label className="form-label">URL du backend</label>
          <input className="form-input" value={apiUrl} onChange={e => setApiUrl(e.target.value)} placeholder="http://localhost:3000/api/v1" />
          <div className="form-hint">Variable d'environnement: REACT_APP_API_URL</div>
        </div>
        <div className="inline-alert inline-alert-blue" style={{ marginBottom: 16 }}>
          <span>ℹ</span>
          <span style={{ fontSize: 12 }}>Pour changer l'URL en production, modifiez le fichier <code style={{ background: 'var(--bg4)', padding: '1px 5px', borderRadius: 3 }}>.env</code> et redémarrez l'application.</span>
        </div>
        <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Endpoints principaux</div>
          {[
            { method: 'POST', path: '/auth/login' },
            { method: 'GET',  path: '/admin/dashboard' },
            { method: 'GET',  path: '/admin/pending-validations' },
            { method: 'PUT',  path: '/admin/users/:id/validate' },
            { method: 'POST', path: '/admin/laveries' },
            { method: 'POST', path: '/admin/livreurs' },
            { method: 'POST', path: '/admin/discounts' },
          ].map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, fontSize: 12, padding: '4px 0', borderBottom: i < 6 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ color: e.method === 'GET' ? 'var(--green)' : e.method === 'POST' ? 'var(--blue)' : 'var(--orange)', fontWeight: 700, width: 36 }}>{e.method}</span>
              <span style={{ color: 'var(--text2)', fontFamily: 'monospace' }}>{apiUrl}{e.path}</span>
            </div>
          ))}
        </div>
      </div>

      {/* SECURITY INFO */}
      <div className="card">
        <div className="card-header"><div className="card-title">Sécurité — Filtre d'accès admin</div></div>
        <div className="inline-alert inline-alert-green" style={{ marginBottom: 16 }}>
          <span>🛡️</span>
          <div>
            <strong>Protection active.</strong> Le filtre admin est activé sur cette plateforme.
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
          <p style={{ marginBottom: 12 }}>
            L'API backend LAVEX utilise un endpoint d'authentification générique (<code className="text-mono">/auth/login</code>) partagé entre tous les types d'utilisateurs (clients, livreurs, laveries, admins).
          </p>
          <p style={{ marginBottom: 12 }}>
            Cette plateforme web intercepte la réponse et vérifie que <code className="text-mono">role === "admin"</code> avant d'autoriser l'accès. Tout autre rôle reçoit un message d'erreur explicite.
          </p>
          <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, marginTop: 8 }}>
            <span style={{ color: 'var(--green)' }}>// AuthContext.js — ligne de filtre</span><br />
            <span style={{ color: 'var(--blue)' }}>if</span> (u.role !== <span style={{ color: 'var(--accent)' }}>'admin'</span>) {'{'}<br />
            &nbsp;&nbsp;<span style={{ color: 'var(--text2)' }}>setError</span>(<span style={{ color: 'var(--accent)' }}>"Accès refusé..."</span>);<br />
            &nbsp;&nbsp;<span style={{ color: 'var(--blue)' }}>return false</span>;<br />
            {'}'}
          </div>
        </div>
      </div>
    </div>
  );
};
